import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Loader, AlertCircle, CheckCircle, Eye, EyeOff, MessageCircle, ChevronRight, Zap, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import BrandLogo from '../components/BrandLogo';
import Seo from '../components/Seo';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
        <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)] text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-[#BA0517]/10 border border-[#BA0517]/20 rounded-full flex items-center justify-center">
             <AlertCircle size={40} className="text-[#BA0517]" />
          </div>
          <div className="space-y-2">
             <h2 className="text-xl font-bold text-[#080707] tracking-tight">Invalid Reset Link</h2>
             <p className="text-sm text-[#706E6B] font-medium leading-relaxed">
               This password reset link is missing a valid security token.
             </p>
          </div>
          <Link to="/forgot-password" className="w-full h-9 inline-flex items-center justify-center bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486]">
             Request New Link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) { setError('Password needs at least 8 characters with an uppercase letter, a lowercase letter, a number and a special character'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
      <Seo
        title="Reset Password | WaapLeads CRM"
        description="Choose a new password for your WaapLeads CRM account and restore secure access."
        pathname="/reset-password"
      />
      {/* ── RIGHT SIDE: RESET FORM ── */}
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full">
          {!token ? (
            <div className="text-center space-y-8 py-4 animate-nebula-fade-up">
              <div className="w-20 h-20 mx-auto bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center justify-center shadow-sm shadow-red-500/10 mb-8">
                 <AlertCircle size={40} className="text-[#BA0517]" />
              </div>
              <div className="space-y-3">
                 <h2 className="text-xl font-bold text-[#2E2E2E]">Invalid Link</h2>
                 <p className="text-[13px] text-[#5C5C5C] font-medium leading-relaxed max-w-xs mx-auto">
                   This password reset link is missing a valid security token or has expired.
                 </p>
              </div>
              <Link to="/forgot-password" className="w-full flex items-center justify-center gap-3 h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50">
                Request New Link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-8 py-4 animate-nebula-fade-up">
              <div className="w-20 h-20 mx-auto bg-[#EBF7E6] border border-[#CDEFC4] rounded-lg flex items-center justify-center shadow-sm shadow-green-500/10 mb-8">
                 <CheckCircle size={40} className="text-[#2E844A]" />
              </div>
              <div className="space-y-3">
                 <h2 className="text-xl font-bold text-[#2E2E2E]">Password Reset</h2>
                 <p className="text-[13px] text-[#5C5C5C] font-medium leading-relaxed max-w-xs mx-auto">
                   Your secret key has been updated successfully. Redirecting to terminal authentication...
                 </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center mb-6 text-center">
                <BrandLogo size={40} showText={false} className="mb-4" />
                <h1 className="text-xl font-bold text-[#2E2E2E] mb-1">Set New Password</h1>
                <p className="text-[13px] text-[#5C5C5C] font-medium">Choose a new password</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-3 slds-input-group">
                  <label className="text-[11px] font-semibold text-[#706E6B]">New Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] group-focus-within:text-[var(--slds-color-brand)] transition-colors" size={16} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      className="pl-10 pr-12 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none font-mono"
                      placeholder="At least 6 characters"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-[#706E6B] hover:text-[#2E2E2E] transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-3 slds-input-group">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Confirm Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] group-focus-within:text-[var(--slds-color-brand)] transition-colors" size={16} />
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      className="pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none font-mono"
                      placeholder="Repeat new key"
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="w-full flex items-center justify-center gap-3 h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={20} className="animate-spin" />
                  ) : (
                    <>
                      <span>Reset Password</span>
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center pt-4 border-t border-[#E5E5E5]">
                <Link to="/login" className="text-[12px] font-bold text-[#706E6B] hover:text-[#2E2E2E] transition-colors">
                   Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
